import React, { useState } from "react";
import { Container, PageHero } from "@/components/ui-blocks";
import { fetchOrderByLookup } from "@/services/api";
import { DELIVERY_STATUSES } from "@/data/mockData";
import { Check, Circle, AlertTriangle, XCircle } from "lucide-react";
import { toast } from "sonner";

const ORDER_FLOW = [
  "order_received",
  "payment_confirmed",
  "preparing",
  "ready_for_dispatch",
  "shipped",
  "out_for_delivery",
  "delivered",
];

export default function TrackOrder() {
  const [orderId, setOrderId] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!orderId && !email) {
      toast.error("Enter your order number or email");
      return;
    }
    setLoading(true);
    const result = await fetchOrderByLookup({ orderId, email });
    setLoading(false);
    if (!result) {
      toast.error("Order not found", { description: "Try: AA-2026-00141 or anjali.menon@example.com" });
      setOrder(null);
      return;
    }
    setOrder(result);
  };

  return (
    <div data-testid="track-order-page">
      <PageHero
        testId="track-hero"
        overline="Order Status"
        title="Track your order."
        subtitle="Enter your order number or email to view the latest delivery status."
      />

      <section className="bg-[#FDFBF7] py-16">
        <Container>
          <form onSubmit={handleLookup} data-testid="track-form" className="max-w-2xl grid sm:grid-cols-2 gap-6">
            <label className="block">
              <span className="text-xs uppercase tracking-[0.15em] text-[#4A3728]">Order number</span>
              <input
                data-testid="track-order-id-input"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="AA-2026-00141"
                className="w-full bg-transparent border-b border-[#6e5022]/40 focus:border-[#6e5022] py-2.5 outline-none text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.15em] text-[#4A3728]">Or your email</span>
              <input
                data-testid="track-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-transparent border-b border-[#6e5022]/40 focus:border-[#6e5022] py-2.5 outline-none text-sm"
              />
            </label>
            <div className="sm:col-span-2">
              <button
                data-testid="track-lookup-button"
                type="submit"
                className="bg-[#6e5022] hover:bg-[#946f36] text-[#FDFBF7] px-7 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors"
              >
                {loading ? "Looking up…" : "Track Order"}
              </button>
              <span className="ml-4 text-xs text-[#946f36]">
                Try demo: AA-2026-00143
              </span>
            </div>
          </form>

          {order && (
            <div className="mt-12 border border-[#6e5022]/25 p-8" data-testid="track-result">
              <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-[#6e5022]/20">
                <div>
                  <div className="overline text-[#946f36]">Order</div>
                  <div data-testid="track-result-order-id" className="font-serif-display text-3xl">{order.orderId}</div>
                  <div className="text-sm text-[#4A3728] mt-1">{order.customerName} · {order.email}</div>
                </div>
                <div className="text-right">
                  <div className="overline text-[#946f36]">Total</div>
                  <div data-testid="track-result-total" className="font-serif-display text-2xl">SGD {order.total.toFixed(2)}</div>
                </div>
              </div>

              <Timeline status={order.deliveryStatus} />

              {order.trackingNotes && (
                <div className="mt-8 bg-[#F4F1EA] p-5 border-l-2 border-[#946f36]" data-testid="track-result-notes">
                  <div className="overline text-[#946f36] mb-1">Tracking note</div>
                  <div className="text-sm text-[#4A3728]">{order.trackingNotes}</div>
                </div>
              )}
              <div className="mt-4 text-xs text-[#946f36]">
                Last updated: {new Date(order.updatedAt).toLocaleString("en-SG")}
              </div>
            </div>
          )}
        </Container>
      </section>
    </div>
  );
}

function Timeline({ status }) {
  const isCancelled = status === "cancelled";
  const isDelayed = status === "delayed";
  const currentIndex = ORDER_FLOW.indexOf(status);

  return (
    <div className="mt-8" data-testid="track-timeline">
      {isCancelled && (
        <div className="mb-6 flex items-center gap-3 text-red-800 bg-red-900/5 border border-red-900/30 p-4">
          <XCircle className="w-5 h-5" strokeWidth={1.5} />
          <span>This order has been cancelled.</span>
        </div>
      )}
      {isDelayed && (
        <div className="mb-6 flex items-center gap-3 text-amber-800 bg-amber-100/40 border border-amber-700/30 p-4">
          <AlertTriangle className="w-5 h-5" strokeWidth={1.5} />
          <span>Your order is currently delayed. We&apos;ll update you shortly.</span>
        </div>
      )}
      <ol className="space-y-5">
        {ORDER_FLOW.map((s, i) => {
          const meta = DELIVERY_STATUSES.find((d) => d.key === s);
          const isDone = !isCancelled && currentIndex >= i;
          const isCurrent = currentIndex === i;
          return (
            <li key={s} className="flex items-start gap-4" data-testid={`timeline-step-${s}`}>
              <span
                className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center border ${
                  isDone
                    ? "bg-[#6e5022] border-[#6e5022] text-[#FDFBF7]"
                    : "bg-[#FDFBF7] border-[#6e5022]/30 text-[#6e5022]"
                }`}
              >
                {isDone ? <Check className="w-3 h-3" strokeWidth={2.5} /> : <Circle className="w-2 h-2 fill-current" />}
              </span>
              <div className="flex-1">
                <div className={`font-serif-display text-xl ${isCurrent ? "text-[#281a12]" : isDone ? "text-[#4A3728]" : "text-[#946f36]"}`}>
                  {meta.label}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
